/**
 * Edgemail brand marks.
 *
 * Two assets shipped in /public (both have generous baked-in whitespace):
 *   - /edgelogo.png        → full lockup (icon + wordmark), 1343×800
 *   - /edgelogo_icon.png   → icon only, 283×283
 *
 * The PNGs are dark/colored by default. On dark surfaces, pass `invert` so
 * CSS `brightness(0) invert(1)` turns them white — avoids a separate white
 * asset.
 *
 * ## Tight-crop rendering
 *
 * Both assets ship with padding around the mark. To make the logo fill the
 * height it's given (matching the app sidebar's treatment), we render into
 * an `overflow-hidden` box and scale the inner `<img>` up. The visual is
 * anchored at the left-middle so horizontal layout stays predictable.
 *
 * - Container height = `size`
 * - Container width  = `size × aspect × SCALE` (just enough to show the
 *   full scaled lockup without right-side clipping; ~3.2× size for full
 *   variant, ~1.9× size for icon)
 * - Inner img uses `h-full w-auto`, transformed 1.9× from origin `0 50%`
 *
 * This keeps a single source of truth for the brand appearance across the
 * landing page, sign-in page, and the authenticated app shell.
 */

import { cn } from '@/lib/utils';

type Variant = 'full' | 'icon';

/** Visual scale applied to the inner img to crop out baked-in whitespace. */
const SCALE: Record<Variant, number> = {
  full: 1.9,
  icon: 1,
};

/** Natural aspect (w/h) of each asset, used to size the cropped viewport. */
const ASPECT: Record<Variant, number> = {
  full: 1343 / 800,
  icon: 1,
};

export function Logo({
  variant = 'full',
  invert = false,
  className,
  size = 24,
}: {
  variant?: Variant;
  invert?: boolean;
  className?: string;
  /** Rendered height of the crop viewport in px. */
  size?: number;
}) {
  const src = variant === 'full' ? '/edgelogo.png' : '/edgelogo_icon.png';
  const scale = SCALE[variant];
  const width = size * ASPECT[variant] * scale;
  return (
    <span
      role="img"
      aria-label="edgemail"
      className={cn(
        'relative inline-block overflow-hidden align-middle shrink-0 select-none',
        className,
      )}
      style={{ height: size, width }}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        className={cn(
          'block h-full w-auto max-w-none pointer-events-none',
          invert && 'brightness-0 invert',
        )}
        style={{ transformOrigin: '0 50%', transform: `scale(${scale})` }}
      />
    </span>
  );
}
